import React, { useState } from 'react';
import Radio from '@material-ui/core/Radio';
import { FormControlLabel } from '@material-ui/core';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import CircularProgress from '@mui/material/CircularProgress';
import swal from 'sweetalert';
import { analyzeContracts } from '../thunks/analyzeContracts';
import CountryCodeDropdown, { LocationPayloadPreview } from '../components/CountryCodeDropdown';
import { getAirportsByCountry, LocationPayload } from '../thunks/getAirportsByCountry';

type AnalyzeFormState = {
    origin: string;
    destination: string;
    carrier: string;
    tripType: 'OW' | '-OW';
    cabinType: string;
    fareType: string;
    landFare: number;
    cruiseFare: number;
};

type AnalyzePreprocess = {
    carrier?: string;
    tripType?: string;
    cabinType?: string;
    contractType?: string;
    landFare?: number;
    cruiseFare?: number;
    origin?: {
        countryCode?: string;
        queryValues?: string[];
        airports?: LocationPayload['airports'];
    };
    destination?: {
        countryCode?: string;
        queryValues?: string[];
        airports?: LocationPayload['airports'];
    };
};

type AnalyzeContractRow = {
    tarifKey: string;
    clasKey: string;
    prixKey: string;
    origins: string;
    destinations: string;
    brandCode: string;
    classCodes: string | string[];
    classOfService?: string;
    matchType?: string;
    groupLabel?: string;
    contractType: string;
    commissionPercentage: string | number;
    passBackCommission: string | number;
    markup: string | number;
};

const emptyForm = (): AnalyzeFormState => ({
    origin: '',
    destination: '',
    carrier: '',
    tripType: 'OW',
    cabinType: 'Economy',
    fareType: 'Published',
    landFare: 0,
    cruiseFare: 0,
});

const isLandAndCruise = (form: AnalyzeFormState) => form.landFare === 1 && form.cruiseFare === 1;

const formatList = (value: string | string[] | null | undefined) => {
    if (Array.isArray(value)) {
        return value.filter(Boolean).join(', ');
    }
    return value || '—';
};

const formatLocationChip = (label: string, location?: AnalyzePreprocess['origin']) => {
    if (!location) {
        return null;
    }
    return `${label}: ${location.countryCode || '—'} / ${(location.queryValues || []).length} query values`;
};

const parseAgencyCommission = (value: string | number | null | undefined): number | null => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const numeric = Number(String(value).replace(/[%$\s]/g, ''));
    if (Number.isNaN(numeric)) {
        return null;
    }
    return Math.abs(numeric);
};

const groupRowsByCos = (rows: AnalyzeContractRow[]) => {
    const groups = new Map<string, { cos: string; onlyCos: AnalyzeContractRow | null; branded: AnalyzeContractRow[] }>();
    rows.forEach((row) => {
        const cos = row.classOfService || '—';
        if (!groups.has(cos)) {
            groups.set(cos, { cos, onlyCos: null, branded: [] });
        }
        const group = groups.get(cos);
        if (!group) {
            return;
        }
        if (row.matchType === 'Only COS') {
            group.onlyCos = row;
        } else {
            group.branded.push(row);
        }
    });
    return Array.from(groups.values()).sort((left, right) => left.cos.localeCompare(right.cos));
};

const openContractLink = (event: React.MouseEvent, url: string) => {
    event.stopPropagation();
    window.open(url, '_blank');
};

const ContractCells = ({
    row,
    isPublished,
    showBrand,
}: {
    row: AnalyzeContractRow;
    isPublished: boolean;
    showBrand: boolean;
}) => (
    <>
        <TableCell>{row.classOfService || formatList(row.classCodes)}</TableCell>
        <TableCell>{showBrand ? (row.brandCode || '—') : '—'}</TableCell>
        <TableCell>
            <span
                className="cm-primary-link"
                onClick={(event) => openContractLink(event, `/newTariff?tariff=${row.tarifKey}`)}
            >
                {row.tarifKey}
            </span>
        </TableCell>
        <TableCell>
            <span
                className="cm-primary-link"
                onClick={(event) => openContractLink(event, `/newClass?tariff=${row.tarifKey}&class=${row.clasKey}`)}
            >
                {row.clasKey}
            </span>
        </TableCell>
        <TableCell>
            <span
                className="cm-primary-link"
                onClick={(event) => openContractLink(event, `/newPrix?tariff=${row.tarifKey}&class=${row.clasKey}&prix=${row.prixKey}`)}
            >
                {row.prixKey}
            </span>
        </TableCell>
        <TableCell>{row.origins} → {row.destinations}</TableCell>
        {isPublished ? (
            <>
                <TableCell>{row.commissionPercentage || '—'}</TableCell>
                <TableCell>{row.passBackCommission || '—'}</TableCell>
            </>
        ) : (
            <TableCell>{row.markup || '—'}</TableCell>
        )}
    </>
);

const MatchTable = ({
    title,
    rows,
    fareType,
}: {
    title: string;
    rows: AnalyzeContractRow[];
    fareType: string;
}) => {
    const isPublished = fareType === 'Published';
    const groups = groupRowsByCos(rows);
    const onlyCosCommissionByClass = rows.reduce<Record<string, number>>((acc, row) => {
        if (row.matchType === 'Only COS' && row.classOfService) {
            const commission = parseAgencyCommission(row.commissionPercentage);
            if (commission !== null) {
                acc[row.classOfService] = commission;
            }
        }
        return acc;
    }, {});

    const shouldHighlightBrandedRow = (row: AnalyzeContractRow) => {
        if (!isPublished || row.matchType !== 'COS + brand code' || !row.classOfService) {
            return false;
        }
        const brandedCommission = parseAgencyCommission(row.commissionPercentage);
        const onlyCosCommission = onlyCosCommissionByClass[row.classOfService];
        return brandedCommission !== null && onlyCosCommission !== undefined && brandedCommission < onlyCosCommission;
    };

    return (
    <div className="card cm-card mt-4" style={{ padding: 10 }}>
        <div className="card-body">
            <div className="cm-head-2">
                <div className="d-flex justify-content-between align-items-center">
                    <span>{title}</span>
                    <span className="cm-head-label mb-0">{groups.length} COS group{groups.length === 1 ? '' : 's'}</span>
                </div>
            </div>
            {isPublished && (
                <div className="mt-2" style={{ fontSize: 12, color: '#d32f2f' }}>
                    Highlighted branded rows have a lower agency commission than the Class ONLY match.
                </div>
            )}
            {groups.length === 0 ? (
                <div className="text-muted mt-3">No matching contracts.</div>
            ) : (
                <div className="mt-3">
                    {groups.map((group) => {
                        const headerRow = group.onlyCos;
                        const detailRows = [
                            ...(group.onlyCos ? [group.onlyCos] : []),
                            ...group.branded,
                        ];
                        return (
                            <Accordion key={group.cos} className="mb-2">
                                <AccordionSummary>
                                    <div className="d-flex flex-wrap align-items-center w-100" style={{ gap: 16 }}>
                                        <strong>{group.cos} Class ONLY match</strong>
                                        {headerRow ? (
                                            <>
                                                <span>{headerRow.tarifKey}</span>
                                                {isPublished ? (
                                                    <>
                                                        <span>Our Commission: {headerRow.commissionPercentage || '—'}</span>
                                                        <span>Passback: {headerRow.passBackCommission || '—'}</span>
                                                    </>
                                                ) : (
                                                    <span>Markup: {headerRow.markup || '—'}</span>
                                                )}
                                            </>
                                        ) : (
                                            <span className="text-muted">No Only COS match</span>
                                        )}
                                        <span className="ml-auto cm-head-label mb-0">
                                            {group.branded.length} branded
                                        </span>
                                    </div>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <TableContainer component={Paper}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Class</TableCell>
                                                    <TableCell>Brand</TableCell>
                                                    <TableCell>Tariff</TableCell>
                                                    <TableCell>Class</TableCell>
                                                    <TableCell>Prix</TableCell>
                                                    <TableCell>OD</TableCell>
                                                    {isPublished ? (
                                                        <>
                                                            <TableCell>Our Commission</TableCell>
                                                            <TableCell>Passback Commission</TableCell>
                                                        </>
                                                    ) : (
                                                        <TableCell>Markup</TableCell>
                                                    )}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {detailRows.map((row, index) => (
                                                    <TableRow
                                                        key={`${row.groupLabel}-${row.tarifKey}-${row.prixKey}-${index}`}
                                                        style={shouldHighlightBrandedRow(row) ? { backgroundColor: '#FFF3CD' } : undefined}
                                                    >
                                                        <ContractCells
                                                            row={row}
                                                            isPublished={isPublished}
                                                            showBrand={row.matchType !== 'Only COS'}
                                                        />
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </div>
            )}
        </div>
    </div>
    );
};

const AnalyzeContracts = () => {
    const [form, setForm] = useState<AnalyzeFormState>(emptyForm);
    const [loading, setLoading] = useState(false);
    const [hasAnalyzed, setHasAnalyzed] = useState(false);
    const [preprocess, setPreprocess] = useState<AnalyzePreprocess | null>(null);
    const [contracts, setContracts] = useState<AnalyzeContractRow[]>([]);
    const [originLocation, setOriginLocation] = useState<LocationPayload | null>(null);
    const [destinationLocation, setDestinationLocation] = useState<LocationPayload | null>(null);

    const updateField = (name: keyof AnalyzeFormState, value: string | number | boolean) => {
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const loadLocationPayload = async (
        countryCode: string,
        setter: (payload: LocationPayload | null) => void
    ) => {
        if (!countryCode) {
            setter(null);
            return;
        }
        const payload = await getAirportsByCountry(countryCode);
        setter(payload);
    };

    const handleTextChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = event.target;
        const uppercasedFields = ['carrier'];
        updateField(
            name as keyof AnalyzeFormState,
            uppercasedFields.includes(name) ? value.toUpperCase() : value
        );
    };

    const handleAnalyze = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!form.origin.trim() || !form.destination.trim() || !originLocation || !destinationLocation || !form.carrier.trim()) {
            swal({
                text: 'Please fill origin, destination, and carrier.',
                icon: 'error',
                timer: 2500,
            });
            return;
        }

        setLoading(true);
        const payload = {
            origin: form.origin.trim(),
            destination: form.destination.trim(),
            originLocation,
            destinationLocation,
            carrier: form.carrier.trim(),
            tripType: form.tripType,
            cabinType: form.cabinType,
            contractType: form.fareType,
            landFare: form.landFare,
            cruiseFare: form.cruiseFare,
        };
        const result = await analyzeContracts(payload);
        setLoading(false);
        if (!result) {
            return;
        }
        setHasAnalyzed(true);
        setPreprocess(result.preprocess || null);
        setContracts(result.contracts || []);
    };

    const handleClear = () => {
        setForm(emptyForm());
        setHasAnalyzed(false);
        setPreprocess(null);
        setContracts([]);
        setOriginLocation(null);
        setDestinationLocation(null);
    };

    return (
        <div>
            <div className="card cm-card" style={{ padding: 10 }}>
                <div className="card-body">
                    <div className="cm-head-2">
                        <div className="d-flex justify-content-between">
                            <span>Contracts QC</span>
                        </div>
                    </div>
                    <form onSubmit={handleAnalyze}>
                        <div className="row mt-4">
                            <div className="col-md-4 cm-col">
                                <label className="cm-head-label">Origin</label>
                                <CountryCodeDropdown
                                    label="Origin"
                                    hideLabel={true}
                                    value={form.origin}
                                    required={true}
                                    placeholder="Select country code"
                                    onChange={(countryCode) => {
                                        updateField('origin', countryCode);
                                        loadLocationPayload(countryCode, setOriginLocation);
                                    }}
                                />
                            </div>
                            <div className="col-md-4 cm-col">
                                <label className="cm-head-label">Destination</label>
                                <CountryCodeDropdown
                                    label="Destination"
                                    hideLabel={true}
                                    value={form.destination}
                                    required={true}
                                    placeholder="Select country code"
                                    onChange={(countryCode) => {
                                        updateField('destination', countryCode);
                                        loadLocationPayload(countryCode, setDestinationLocation);
                                    }}
                                />
                            </div>
                            <div className="col-md-4 cm-col">
                                <label className="cm-head-label">Carrier</label>
                                <input
                                    className="form-control cm-input"
                                    name="carrier"
                                    placeholder="Enter carrier"
                                    value={form.carrier}
                                    onChange={handleTextChange}
                                />
                            </div>
                        </div>

                        <div className="row mt-3">
                            <div className="col-md-4 cm-col">
                                <label className="cm-head-label">Trip Type</label>
                                <div className="cm-radio-inline">
                                    <FormControlLabel
                                        control={
                                            <Radio
                                                color="primary"
                                                checked={form.tripType === 'OW'}
                                                onClick={() => updateField('tripType', 'OW')}
                                            />
                                        }
                                        label="One Way"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Radio
                                                color="primary"
                                                checked={form.tripType === '-OW'}
                                                onClick={() => updateField('tripType', '-OW')}
                                            />
                                        }
                                        label="Not One Way"
                                    />
                                </div>
                            </div>
                            <div className="col-md-4 cm-col">
                                <label className="cm-head-label">Cabin</label>
                                <div className="cm-radio-inline">
                                    <FormControlLabel
                                        control={
                                            <Radio
                                                color="primary"
                                                checked={form.cabinType === 'Economy'}
                                                onClick={() => updateField('cabinType', 'Economy')}
                                            />
                                        }
                                        label="Economy"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Radio
                                                color="primary"
                                                checked={form.cabinType === 'Business'}
                                                onClick={() => updateField('cabinType', 'Business')}
                                            />
                                        }
                                        label="Business"
                                    />
                                </div>
                            </div>
                            <div className="col-md-4 cm-col">
                                <label className="cm-head-label">Fare Type</label>
                                <div className="cm-radio-inline">
                                    <FormControlLabel
                                        control={
                                            <Radio
                                                color="primary"
                                                checked={form.fareType === 'Published'}
                                                onClick={() => updateField('fareType', 'Published')}
                                            />
                                        }
                                        label="Published"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Radio
                                                color="primary"
                                                checked={form.fareType === 'Net'}
                                                onClick={() => updateField('fareType', 'Net')}
                                            />
                                        }
                                        label="Net"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="row mt-3">
                            <div className="col-md-4 cm-col">
                                <label className="cm-head-label">Contract Type</label>
                                <div className="cm-radio-inline">
                                    <FormControlLabel
                                        control={
                                            <Radio
                                                color="primary"
                                                checked={!isLandAndCruise(form)}
                                                onClick={() => setForm((prev) => ({ ...prev, landFare: 0, cruiseFare: 0 }))}
                                            />
                                        }
                                        label="Air"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Radio
                                                color="primary"
                                                checked={isLandAndCruise(form)}
                                                onClick={() => setForm((prev) => ({ ...prev, landFare: 1, cruiseFare: 1 }))}
                                            />
                                        }
                                        label="Land & Cruise"
                                    />
                                </div>
                            </div>
                        </div>

                        {(originLocation || destinationLocation) && (
                            <div className="row mt-3">
                                <div className="col-md-6 cm-col">
                                    <LocationPayloadPreview title="Origin payload" payload={originLocation} />
                                </div>
                                <div className="col-md-6 cm-col">
                                    <LocationPayloadPreview title="Destination payload" payload={destinationLocation} />
                                </div>
                            </div>
                        )}

                        <div className="d-flex mt-4">
                            <button type="submit" className="cm-primary-btn mr-2" disabled={loading}>
                                {loading ? 'Analyzing...' : 'ANALYZE'}
                            </button>
                            <button type="button" className="cm-secondary-btn" onClick={handleClear} disabled={loading}>
                                Clear
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {loading && (
                <div className="d-flex align-items-center mt-4">
                    <CircularProgress size={22} style={{ marginRight: 10 }} />
                    <span>Matching contracts...</span>
                </div>
            )}

            {hasAnalyzed && !loading && (
                <>
                    {preprocess && (
                        <div className="card cm-card mt-4" style={{ padding: 10 }}>
                            <div className="card-body">
                                <div className="cm-head-2">
                                    <span>Preprocess</span>
                                </div>
                                <div className="d-flex flex-wrap mt-3">
                                    {preprocess.carrier && (
                                        <span className="badge badge-light mr-2 mb-2 p-2">Carrier: {preprocess.carrier}</span>
                                    )}
                                    {preprocess.tripType && (
                                        <span className="badge badge-light mr-2 mb-2 p-2">Trip: {preprocess.tripType}</span>
                                    )}
                                    {preprocess.cabinType && (
                                        <span className="badge badge-light mr-2 mb-2 p-2">Cabin: {preprocess.cabinType}</span>
                                    )}
                                    {preprocess.contractType && (
                                        <span className="badge badge-light mr-2 mb-2 p-2">Fare type: {preprocess.contractType}</span>
                                    )}
                                    <span className="badge badge-light mr-2 mb-2 p-2">
                                        Contract type: {preprocess.landFare === 1 && preprocess.cruiseFare === 1 ? 'Land & Cruise' : 'Air'}
                                    </span>
                                    <span className="badge badge-light mr-2 mb-2 p-2">
                                        {formatLocationChip('Origin', preprocess.origin)}
                                    </span>
                                    <span className="badge badge-light mb-2 p-2">
                                        {formatLocationChip('Destination', preprocess.destination)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                    <MatchTable title="Contracts" rows={contracts} fareType={form.fareType} />
                </>
            )}
        </div>
    );
};

export default AnalyzeContracts;
